import type { CameraData } from '@/entities/model3d'
import type { DeepReadonly, Ref } from 'vue'

export interface UseEmbedPriorityCameras {
  priorityCameras: Ref<DeepReadonly<CameraData[]>>
}

export function useEmbedPriorityCameras({
  embedId,
  deckId,
  isAnimatedDeck,
}: {
  embedId: string
  deckId: Ref<string | number | undefined>
  isAnimatedDeck: Ref<boolean>
}): UseEmbedPriorityCameras {
  const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({}))
  const priorityCameras: Ref<CameraData[]> = ref([])
  const route = useRoute()

  let hasGottenAllTheCamerasNecessary = false

  watch(
    [deckId, isAnimatedDeck],
    async () => {
      if (hasGottenAllTheCamerasNecessary) return

      const config = {
        headers: { ...globalFetchHeaders.value, 'X-Deck-Auth-UUID': (route.query?.auth as string) ?? null },
      }

      const isInAnimatedView = !deckId.value && isAnimatedDeck.value
      if (isInAnimatedView) {
        const data = await $fetch<{ results: CameraData[] }>(`v2/embed/decks/${embedId}/priority_cameras?limit=5000`, {
          ...useFetchSharedOptions(),
          ...config,
        })
        priorityCameras.value = data.results
        return
      }

      const isInLightbox = deckId.value && isAnimatedDeck.value
      if (isInLightbox) {
        const data = await $fetch<{ results: CameraData[] }>(`v2/decks/${deckId.value}/priority_cameras?limit=5000`, {
          ...useFetchSharedOptions(),
          ...config,
        })
        priorityCameras.value = data.results
        hasGottenAllTheCamerasNecessary = true
        return
      }

      if (deckId.value && !isAnimatedDeck.value) {
        const data = await $fetch<{ results: CameraData[] }>(`v2/decks/${deckId.value}/priority_cameras?limit=5000`, {
          ...useFetchSharedOptions(),
          ...config,
        })
        priorityCameras.value = data.results
        return
      }
    },
    { immediate: true }
  )

  return { priorityCameras: readonly(priorityCameras) }
}
